import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import theme from '@/config/theme';

interface FirstChecklistOnboardingProps {
  visible: boolean;
  companyName?: string | null;
  usingDefaultCompany?: boolean;
  onDismiss: () => void;
}

export const FirstChecklistOnboarding: React.FC<FirstChecklistOnboardingProps> = ({
  visible,
  onDismiss,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <LinearGradient colors={theme.colors.primary.gradient} style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <Text style={styles.pill}>Primeiros passos</Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onDismiss}
              accessibilityRole="button"
              accessibilityLabel="Fechar onboarding inicial"
            >
              <Ionicons name="close" size={18} color={theme.colors.primary.contrast} />
            </TouchableOpacity>
          </View>
          <Text style={styles.title}>4 modelos prontos para testar</Text>
        </View>

        <Text style={styles.subtitle}>
          Seu teste grátis já inclui modelos de carro, moto, caminhão e cavalo mecânico para você ver como funciona.
        </Text>

        <View style={styles.tipRow}>
          <View style={styles.tipIcon}>
            <Ionicons name="options" size={18} color={theme.colors.primary.default} />
          </View>
          <View style={styles.tipTextContainer}>
            <Text style={styles.tipTitle}>Modelos adaptados à sua operação</Text>
            <Text style={styles.tipDescription}>
              Ao decidir assinar o app, adaptamos os modelos para mostrar apenas o que sua empresa precisa e também podemos criar novos modelos para você.
            </Text>
          </View>
        </View>
        <View style={styles.tipRow}>
          <View style={styles.tipIcon}>
            <Ionicons name="calendar-outline" size={18} color={theme.colors.primary.default} />
          </View>
          <View style={styles.tipTextContainer}>
            <Text style={styles.tipTitle}>Mensal e sem fidelidade</Text>
            <Text style={styles.tipDescription}>
              Você pode cancelar quando quiser. Para testar grátis, toque em “Criar checklist”.
            </Text>
          </View>
        </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  card: {
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    ...theme.shadows.md,
  },
  header: {
    marginBottom: theme.spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  pill: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: theme.colors.primary.contrast,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 2,
    borderRadius: theme.borderRadius.pill,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
    alignSelf: 'flex-start',
    marginBottom: theme.spacing.xs,
  },
  title: {
    color: theme.colors.primary.contrast,
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.semibold as any,
    flexWrap: 'wrap' as const,
    lineHeight: 28,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: theme.fontSizes.md,
    marginBottom: theme.spacing.md,
    lineHeight: 20,
  },
  tipRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
  },
  tipIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.colors.primary.contrast,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.sm,
  },
  tipTextContainer: {
    flex: 1,
  },
  tipTitle: {
    color: theme.colors.primary.contrast,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  tipDescription: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: theme.fontSizes.sm,
    marginTop: 2,
    lineHeight: 18,
  },
});

export default FirstChecklistOnboarding;
